import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaInstagram, FaClock } from 'react-icons/fa';

const ContactContainer = styled.div`
  padding: 80px 0 60px;
  background: var(--background-dark);
`;

const ContactHero = styled.div`
  background: linear-gradient(135deg, var(--background-accent) 0%, var(--background-lighter) 100%);
  padding: 60px 0;
  text-align: center;
  margin-bottom: 80px;
  border-bottom: 5px solid var(--primary-color);
`;

const ContactHeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ContactHeroTitle = styled(motion.h1)`
  font-size: 2.8rem;
  color: var(--text-primary);
  margin-bottom: 20px;
  
  span {
    color: var(--primary-color);
  }
`;

const ContactHeroDescription = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 0;
`;

const ContactSection = styled.section`
  padding: 60px 0;
  background: transparent;
`;

const ContactContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div``;

const ContactTitle = styled(motion.h2)`
  font-size: 2.2rem;
  color: var(--text-primary);
  margin-bottom: 30px;
  
  span {
    color: var(--primary-color);
  }
`;

const ContactInfoList = styled.div`
  margin-bottom: 40px;
`;

const ContactInfoItem = styled.div`
  display: flex;
  margin-bottom: 25px;
`;

const ContactInfoIcon = styled.div`
  width: 50px;
  height: 50px;
  background-color: rgba(107, 99, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
  
  svg {
    font-size: 1.2rem;
    color: var(--primary-color);
  }
`;

const ContactInfoContent = styled.div``;

const ContactInfoTitle = styled.h4`
  font-size: 1.1rem;
  color: var(--text-primary);
  margin-bottom: 5px;
`;

const ContactInfoText = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 0;
  
  a {
    color: var(--text-secondary);
    transition: var(--transition);
    
    &:hover {
      color: var(--primary-color);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  margin-top: 30px;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--background-lighter);
  color: var(--text-secondary);
  margin-right: 15px;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--primary-color);
    color: white;
    transform: translateY(-3px);
  }
`;

const ContactForm = styled(motion.form)`
  background-color: var(--background-lighter);
  padding: 40px;
  border-radius: var(--border-radius);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  background-color: var(--background-dark);
  border: 1px solid var(--background-lighter);
  border-radius: var(--border-radius);
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  background-color: var(--background-dark);
  border: 1px solid var(--background-lighter);
  border-radius: var(--border-radius);
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: var(--transition);
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const FormButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  padding: 12px 25px;
  border-radius: var(--border-radius);
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  border: none;
  
  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-3px);
  }
  
  &:disabled {
    background-color: var(--text-tertiary);
    cursor: not-allowed;
    transform: none;
  }
`;

const FormMessage = styled.div`
  margin-top: 20px;
  padding: 10px;
  border-radius: var(--border-radius);
  font-size: 0.9rem;
  text-align: center;
  
  &.success {
    background-color: rgba(76, 175, 80, 0.1);
    color: var(--success-color);
  }
  
  &.error {
    background-color: rgba(244, 67, 54, 0.1);
    color: var(--error-color);
  }
`;

const MapSection = styled.section`
  padding: 80px 0;
  background-color: var(--background-darker);
`;

const MapContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const MapFrame = styled.div`
  width: 100%;
  height: 450px;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const BusinessHoursSection = styled.section`
  padding: 80px 0;
  background-color: var(--background-dark);
`;

const BusinessHoursContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BusinessHoursCard = styled(motion.div)`
  background-color: var(--background-lighter);
  padding: 40px;
  border-radius: var(--border-radius);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const BusinessHoursTitle = styled.h3`
  font-size: 1.8rem;
  color: var(--text-primary);
  margin-bottom: 30px;
  text-align: center;
  
  svg {
    color: var(--primary-color);
    margin-right: 10px;
  }
`;

const BusinessHoursGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
`;

const BusinessDay = styled.div`
  text-align: center;
  padding: 15px;
  border-radius: var(--border-radius);
  background-color: ${props => props.$isHighlighted ? 'rgba(107, 99, 255, 0.1)' : 'transparent'};
`;

const DayName = styled.h4`
  font-size: 1.1rem;
  color: ${props => props.$isHighlighted ? 'var(--primary-color)' : 'var(--text-primary)'};
  margin-bottom: 5px;
`;

const DayHours = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0;
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });
  
  const [loading, setLoading] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setFormStatus({
          submitted: true,
          success: true,
          message: 'Thank you for your message! We will get back to you soon.'
        });
        
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setFormStatus({
          submitted: true,
          success: false,
          message: data.message || 'Failed to submit form. Please try again.'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Failed to submit form. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <ContactContainer>
      <ContactHero>
        <ContactHeroContent>
          <ContactHeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Get in <span>Touch</span>
          </ContactHeroTitle>
          <ContactHeroDescription
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            We'd love to hear from you! Contact us for any inquiries, to schedule a pickup, or to learn more about our laundry and cleaning services.
          </ContactHeroDescription>
        </ContactHeroContent>
      </ContactHero>

      <ContactSection>
        <ContactContent>
          <ContactInfo>
            <ContactTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Contact <span>Information</span>
            </ContactTitle>
            
            <ContactInfoList>
              <ContactInfoItem>
                <ContactInfoIcon>
                  <FaPhoneAlt />
                </ContactInfoIcon>
                <ContactInfoContent>
                  <ContactInfoTitle>Phone Number</ContactInfoTitle>
                  <ContactInfoText>
                    <a href="tel:9755504400">+91 975 550 4400</a>
                  </ContactInfoText>
                </ContactInfoContent>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactInfoIcon>
                  <FaEnvelope />
                </ContactInfoIcon>
                <ContactInfoContent>
                  <ContactInfoTitle>Email Address</ContactInfoTitle>
                  <ContactInfoText>
                    <a href="mailto:support@istriwala.store">support@istriwala.store</a>
                  </ContactInfoText>
                </ContactInfoContent>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactInfoIcon>
                  <FaMapMarkerAlt />
                </ContactInfoIcon>
                <ContactInfoContent>
                  <ContactInfoTitle>Location</ContactInfoTitle>
                  <ContactInfoText>
                    Opposite Sacha Sauda Gurudwara, near Neta Ji Chowk, Katora Talab, Civil Lines, Raipur, Chhattisgarh
                  </ContactInfoText>
                </ContactInfoContent>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactInfoIcon>
                  <FaInstagram />
                </ContactInfoIcon>
                <ContactInfoContent>
                  <ContactInfoTitle>Instagram</ContactInfoTitle>
                  <ContactInfoText>
                    <a href="https://www.instagram.com/istriwala8" target="_blank" rel="noopener noreferrer">@istriwala8</a>
                  </ContactInfoText>
                </ContactInfoContent>
              </ContactInfoItem>
            </ContactInfoList>
            
            <ContactInfoText>
              Follow us on social media to get updates on promotions, tips for garment care, and more!
            </ContactInfoText>
            
            <SocialLinks>
              <SocialIcon href="https://www.instagram.com/istriwala8" target="_blank" rel="noopener noreferrer">
                <FaInstagram />
              </SocialIcon>
              {/* Add more social icons as needed */}
            </SocialLinks>
          </ContactInfo>
          
          <ContactForm
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onSubmit={handleSubmit}
          >
            <FormGroup>
              <FormLabel htmlFor="name">Full Name</FormLabel>
              <FormInput 
                type="text" 
                id="name" 
                name="name" 
                value={formData.name}
                onChange={handleChange}
                required 
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="email">Email Address</FormLabel>
              <FormInput 
                type="email" 
                id="email" 
                name="email" 
                value={formData.email}
                onChange={handleChange}
                required 
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="phone">Phone Number</FormLabel>
              <FormInput 
                type="tel" 
                id="phone" 
                name="phone" 
                value={formData.phone}
                onChange={handleChange}
                required 
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="subject">Subject</FormLabel>
              <FormInput 
                type="text" 
                id="subject" 
                name="subject" 
                value={formData.subject}
                onChange={handleChange}
                required 
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="message">Message</FormLabel>
              <FormTextarea 
                id="message" 
                name="message" 
                value={formData.message}
                onChange={handleChange}
                required 
              />
            </FormGroup>
            
            <FormButton type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Message'}
            </FormButton>
            
            {formStatus.submitted && (
              <FormMessage className={formStatus.success ? 'success' : 'error'}>
                {formStatus.message}
              </FormMessage>
            )}
          </ContactForm>
        </ContactContent>
      </ContactSection>

      <MapSection>
        <div className="section-title">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Find Us
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Visit our shop or schedule a pickup
          </motion.p>
        </div>
        
        <MapContainer>
          <MapFrame>
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3782.2613173278896!2d73.84864911541669!3d18.52487798741315!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTjCsDMxJzI5LjYiTiA3M8KwNTEnMDEuMiJF!5e0!3m2!1sen!2sin!4v1632389971111!5m2!1sen!2sin" 
              title="Istriwala Location" 
              allowFullScreen="" 
              loading="lazy"
            ></iframe>
          </MapFrame>
        </MapContainer>
      </MapSection>

      <BusinessHoursSection>
        <BusinessHoursContainer>
          <div className="section-title">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Business Hours
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              When you can visit us or schedule pickups
            </motion.p>
          </div>
          
          <BusinessHoursCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <BusinessHoursTitle>
              <FaClock /> Opening Hours
            </BusinessHoursTitle>
            
            <BusinessHoursGrid>
              <BusinessDay>
                <DayName>Monday</DayName>
                <DayHours>9:00 AM - 8:00 PM</DayHours>
              </BusinessDay>
              
              <BusinessDay>
                <DayName>Tuesday</DayName>
                <DayHours>9:00 AM - 8:00 PM</DayHours>
              </BusinessDay>
              
              <BusinessDay>
                <DayName>Wednesday</DayName>
                <DayHours>9:00 AM - 8:00 PM</DayHours>
              </BusinessDay>
              
              <BusinessDay>
                <DayName>Thursday</DayName>
                <DayHours>9:00 AM - 8:00 PM</DayHours>
              </BusinessDay>
              
              <BusinessDay>
                <DayName>Friday</DayName>
                <DayHours>9:00 AM - 8:00 PM</DayHours>
              </BusinessDay>
              
              <BusinessDay $isHighlighted={true}>
                <DayName $isHighlighted={true}>Saturday</DayName>
                <DayHours>10:00 AM - 6:00 PM</DayHours>
              </BusinessDay>
              
              <BusinessDay $isHighlighted={true}>
                <DayName $isHighlighted={true}>Sunday</DayName>
                <DayHours>Closed</DayHours>
              </BusinessDay>
            </BusinessHoursGrid>
          </BusinessHoursCard>
        </BusinessHoursContainer>
      </BusinessHoursSection>
    </ContactContainer>
  );
};

export default Contact;
